import { CommonModule } from '@angular/common';
import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  ReactiveFormsModule,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
} from '@angular/forms';
import { IonIcon, IonList, IonSegment, IonSegmentButton } from '@ionic/angular';
import { tap } from 'rxjs';

import { OneOnOneLanguage } from '@ninety/mobile/platform/api';
import { TextareaComponent } from '@ninety/mobile/platform/components';
import { UserNamePipe } from '@ninety/mobile/platform/services';

import {
  DiscussionModel,
  DiscussionRoleRating,
  DiscussionSeatRating,
  YesNoMaybe,
} from '../../../models/discussion.models';
import * as FormModels from '../../../models/form/discussion-form.model';
import { DiscussionFormMode } from '../../../models/form/discussion-form.model';
import { DiscussionFormBase } from '../discussion-form-base/discussion-form-base';

type RoleRatingsEditableValues = keyof Required<Pick<DiscussionRoleRating, 'getsIt' | 'wantsIt' | 'hasCapacity'>>;
type RoleRatingsFormGroup = FormGroup<Record<RoleRatingsEditableValues, FormControl<YesNoMaybe>>>;

@Component({
  selector: 'nty-role-ratings-form',
  imports: [
    CommonModule,
    IonIcon,
    IonList,
    IonSegment,
    IonSegmentButton,
    ReactiveFormsModule,
    UserNamePipe,
    TextareaComponent,
  ],
  templateUrl: 'discussion-role-ratings-form.component.html',
  styleUrls: ['../../discussion-form-container/common.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DiscussionRoleRatingsFormComponent), multi: true },
  ],
})
export class DiscussionRoleRatingsFormComponent extends DiscussionFormBase implements OnInit, ControlValueAccessor {
  private destroyRef = inject(DestroyRef);
  private managerValue: DiscussionRoleRating[] | DiscussionSeatRating[] = [];
  private manageeValue: DiscussionRoleRating[] | DiscussionSeatRating[] = [];

  @Input({ required: true }) language!: OneOnOneLanguage;
  @Input({ required: true }) discussion!: DiscussionModel;
  @Input() mode: DiscussionFormMode = DiscussionFormMode.Meeting;

  RoleRating = YesNoMaybe;

  form = new FormGroup({
    manager: new FormGroup({
      ratings: new FormArray<RoleRatingsFormGroup>([]),
      notes: new FormControl<string>('', { nonNullable: true }),
    }),
    managee: new FormGroup({
      ratings: new FormArray<RoleRatingsFormGroup>([]),
      notes: new FormControl<string>('', { nonNullable: true }),
    }),
  });

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          if (this.onChange) this.onChange(this.buildChangeResult());
          if (this.onTouched) this.onTouched();
        })
      )
      .subscribe();
  }

  writeValue(val: FormModels.DiscussionRoleRatingsFormValue): void {
    this.managerValue = val.manager.ratings;
    this.manageeValue = val.managee.ratings;

    this.form.reset();
    this.form.patchValue(val);

    val.manager.ratings.forEach(rating =>
      this.managerCtrls.ratings.push(
        new FormGroup({
          getsIt: new FormControl(rating.getsIt, { nonNullable: true, validators: [Validators.required] }),
          wantsIt: new FormControl(rating.wantsIt, { nonNullable: true, validators: [Validators.required] }),
          hasCapacity: new FormControl(rating.hasCapacity, { nonNullable: true, validators: [Validators.required] }),
        })
      )
    );

    val.managee.ratings.forEach(rating =>
      this.manageeCtrls.ratings.push(
        new FormGroup({
          getsIt: new FormControl(rating.getsIt, { nonNullable: true }),
          wantsIt: new FormControl(rating.wantsIt, { nonNullable: true }),
          hasCapacity: new FormControl(rating.hasCapacity, { nonNullable: true }),
        })
      )
    );
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    if (this.mode === DiscussionFormMode.Manager) {
      this.form.controls.managee.disable();
    }

    if (this.mode === DiscussionFormMode.Managee) {
      this.form.controls.manager.disable();
    }
  }

  private buildChangeResult(): FormModels.DiscussionRoleRatingsFormValue {
    return {
      manager: {
        ratings: this.managerCtrls.ratings.value.map((rating, idx) => ({
          ...this.managerValue[idx],
          ...rating,
        })),
        notes: this.managerCtrls.notes.value ?? '',
      },
      managee: {
        ratings: this.manageeCtrls.ratings.value.map((rating, idx) => ({
          ...this.manageeValue[idx],
          ...rating,
        })),
        notes: this.manageeCtrls.notes.value ?? '',
      },
    };
  }

  get managerCtrls() {
    return this.form.controls.manager.controls;
  }

  get manageeCtrls() {
    return this.form.controls.managee.controls;
  }

  get formRoleRatings() {
    const key: keyof Pick<DiscussionModel, 'managerDiscussionForm' | 'manageeDiscussionForm'> = this.isManager
      ? 'managerDiscussionForm'
      : 'manageeDiscussionForm';
    return this.discussion[key]?.roleRatings;
  }

  get ratingOptions() {
    if (this.isEos) {
      return [
        { label: 'Yes', value: this.RoleRating.YES, icon: 'checkmark-outline' },
        { label: 'No', value: this.RoleRating.NO, icon: 'close-outline' },
      ];
    } else {
      return [
        { label: 'Yes', value: this.RoleRating.YES, icon: 'checkmark-outline' },
        { label: 'Maybe', value: this.RoleRating.MAYBE, icon: 'help-outline' },
        { label: 'No', value: this.RoleRating.NO, icon: 'close-outline' },
      ];
    }
  }

  get ccc() {
    return `${this.discussion.conversationLanguage?.CCC ?? ''}: ${this.competency}, ${this.commitment}, ${this.capacity}`;
  }

  get competency() {
    return this.discussion.conversationLanguage?.competency ?? 'Competency';
  }

  get commitment() {
    return this.discussion.conversationLanguage?.commitment ?? 'Commitment';
  }

  get capacity() {
    return this.discussion.conversationLanguage?.capacity ?? 'Capacity';
  }
}
